import * as React from "react";
import { Form, Input, InputOnChangeData, Segment, TextArea, TextAreaProps } from "semantic-ui-react";
import { validate } from "not-valid";
import { isEqual } from "lodash";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";

import { QuestionProps, AnsweredQuestion } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

interface TextQuestionState {
    showError: boolean;
    error: string | null;
}

type TextQuestionProps = QuestionProps & {
    multiline?: boolean;
    rowCount?: number;
    /**
     * @deprecated replaced by `includeTextSnippetSelector` prop
     */
    textSnippetSelector?: React.ReactNode;
    includeTextSnippetSelector?: true;
    useExtendedLabel?: boolean;
    errorMessage?: string; // Set this to override the default error message
}

class TextQuestion extends React.Component<TextQuestionProps, TextQuestionState> {
    constructor(props: QuestionProps) {
        super(props);

        this.state = {
            showError: false,
            error: null
        };

        this.onBlur = this.onBlur.bind(this);
        this.onChange = this.onChange.bind(this);
        this.useSnippet = this.useSnippet.bind(this);
    }

    public render() {
        if (this.props.hidden || isQuestionDisabled(this.props.questionConfig)) {
            return null;
        }

        const error = this.state.showError ? this.state.error : null;

        return (
            <QuestionWrapper
                {...this.props}
                question={this.props.questionConfig.questionText}
                questionElement={this.props.questionConfig.questionElement}
                questionClassName={this.props.questionClassName}
                error={(this.state.showError || this.props.showError) &&
                    ((this.props.mandatory && !(this.props.answer as string).trim() &&
                    (this.props.errorMessage ?? "This field must be completed")) || this.state.error) || undefined}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
                useExtendedLabel={this.props.useExtendedLabel}
            >
                <Form.Field error={!!error}>
                    {this.renderValue()}
                </Form.Field>
            </QuestionWrapper>
        );
    }

    public componentDidMount() {
        this.validateAndUpdateQuestion(this.props.answer as string);
    }

    public componentDidUpdate(oldProps: QuestionProps) {
        if (!isEqual(oldProps.validationDependencies, this.props.validationDependencies)) {
            this.validateAndUpdateQuestion(this.props.answer as string);
        }
    }

    private renderValue() {
        if (this.props.readonly) {
            const highlightedAnswerClass =
                this.props.answerHighlightCriteria && this.props.answerHighlightCriteria(this.props.answer) ? "highlighted-answer" : "";
            return (
                <div className={`text-answer ${highlightedAnswerClass}`.trim()}>
                    {this.props.answer}
                </div>
            );
        }

        const textInput = this.props.multiline ?
            <TextArea value={this.props.answer as string} onChange={this.onChange} onBlur={this.onBlur} rows={this.props.rowCount} /> :
            <Input value={this.props.answer} onChange={this.onChange} onBlur={this.onBlur} />;
        if (this.props.includeTextSnippetSelector) {
            return (
                <>
                    {textInput}
                    <Segment textAlign="left" basic compact>
                        <TextSnippetSelector onSelect={this.useSnippet} />
                    </Segment>
                </>
            );
        }
        if (this.props.textSnippetSelector) { // eslint-disable-line deprecation/deprecation
            return (
                <>
                    {this.props.textSnippetSelector} {/* eslint-disable-line deprecation/deprecation */}
                    {textInput}
                </>
            );
        }
        return textInput;
    }

    private useSnippet = (snippetText: string) => {
        const currentAnswer = this.props.answer;
        this.validateAndUpdateQuestion((currentAnswer ? currentAnswer + " " : "") + snippetText);
    };

    private onBlur() {
        this.setState({ showError: true });
    }

    private onChange(event: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>, data: InputOnChangeData | TextAreaProps) {
        const answer = data.value as string;
        this.validateAndUpdateQuestion(answer);
    }

    private validateAndUpdateQuestion(answer: string) {
        if (this.props.readonly || isQuestionDisabled(this.props.questionConfig)) {
            return;
        }

        const newQuestion: AnsweredQuestion = { question: this.props.questionConfig.questionText, answer };
        if (!(this.props.validators && this.props.validators.length)) {
            newQuestion.isValid = !this.props.mandatory || !!answer?.trim();
        }
        if (this.props.updateQuestion) {
            this.props.updateQuestion(newQuestion);
        }
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }
        if (this.props.validators && this.props.validators.length) {
            this.props.validateQuestion(this.getValidatedQuestion(this.props.questionConfig.questionText, answer));
        }
    }

    private async getValidatedQuestion(question: string, answer: string): Promise<AnsweredQuestion> {
        const errors = await validate(this.props.validators || [], answer);
        this.setState({ error: errors[0] });
        const isValid = errors.length === 0 && (!!answer?.trim() || !this.props.mandatory);
        return { question, answer, isValid };
    }
}

export { TextQuestion };
